import React, { useState } from 'react';

/**
 * Email Exists Modal
 * Shown when user tries to register with an email that already exists
 */
interface EmailExistsModalProps {
  email: string;
  onClose: () => void;
  onResendVerification: () => Promise<void>;
  onSwitchToLogin: () => void;
}

const EmailExistsModal: React.FC<EmailExistsModalProps> = ({
  email,
  onClose,
  onResendVerification,
  onSwitchToLogin,
}) => {
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendError, setResendError] = useState<string | null>(null);

  const handleResend = async () => {
    setIsResending(true);
    setResendError(null);
    setResendSuccess(false);
    
    try {
      await onResendVerification();
      setResendSuccess(true);
      setTimeout(() => {
        setResendSuccess(false);
      }, 3000);
    } catch (err: any) {
      setResendError(err.message || 'Failed to resend verification email. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-gradient-to-br from-slate-950 via-slate-950 to-slate-950 border border-slate-800 rounded-[1.75rem] p-8 max-w-md w-full shadow-[0_24px_40px_rgba(0,0,0,0.55)]">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-[20px] bg-gradient-to-br from-amber-500/20 via-transparent to-transparent border border-slate-800 mb-4">
            <svg
              className="w-8 h-8 text-amber-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h2 className="text-2xl font-semibold text-gray-200 mb-2">
            Account Already Exists
          </h2>
          <p className="text-gray-400 text-sm">
            An account with <span className="text-white font-medium">{email}</span> already exists.
          </p>
        </div>

        {/* Content */}
        <div className="mb-6">
          <p className="text-gray-300 mb-4">
            If you've already signed up, please check your email inbox (including spam folder) for the verification link.
          </p>
          
          {resendSuccess && (
            <div className="mb-4 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
              <p className="text-emerald-400 text-sm">
                ✓ Verification email sent! Please check your inbox.
              </p>
            </div>
          )}

          {resendError && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
              <p className="text-red-400 text-sm">{resendError}</p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="space-y-3">
          <button
            type="button"
            onClick={handleResend}
            disabled={isResending || resendSuccess}
            className="w-full rounded-full px-4 py-3 text-sm font-medium bg-emerald-500 text-slate-950 border border-transparent shadow-[0_10px_18px_rgba(34,197,94,0.28)] hover:bg-emerald-600 hover:shadow-[0_14px_22px_rgba(34,197,94,0.35)] hover:-translate-y-[1px] active:translate-y-0 active:shadow-[0_4px_10px_rgba(34,197,94,0.4)] transition-all duration-150 disabled:opacity-60 disabled:cursor-not-allowed disabled:shadow-none disabled:transform-none"
          >
            {isResending ? 'Sending...' : resendSuccess ? 'Email Sent!' : 'Resend Verification Email'}
          </button>

          <button
            type="button"
            onClick={onSwitchToLogin}
            className="w-full rounded-full px-4 py-3 text-sm font-medium bg-transparent text-gray-200 border border-slate-800 hover:bg-slate-950 hover:border-slate-700 transition-all duration-150"
          >
            Sign In Instead
          </button>

          <button
            type="button"
            onClick={onClose}
            className="w-full rounded-full px-4 py-3 text-sm font-medium text-gray-400 hover:text-gray-200 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailExistsModal;

